import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  FormControlLabel,
  Checkbox,
  Box,
  Alert,
  Chip,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';

/**
 * Double confirmation dialog for when appointment doesn't match preferred settings
 * Supports both time and technician conflicts
 */
const PreferredTimeConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  appointmentInfo,
  preferredTimeInfo,
  newTimeInfo,
  conflicts = [], // Array of { type: 'time'|'technician', message, preferred, actual }
}) => {
  const [step, setStep] = useState(1);
  const [checked1, setChecked1] = useState(false);
  const [checked2, setChecked2] = useState(false);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setStep(1);
      setChecked1(false);
      setChecked2(false);
    }
  }, [open]);

  const handleClose = () => {
    setStep(1);
    setChecked1(false);
    setChecked2(false);
    onClose();
  };

  const handleNext = () => {
    if (step === 1 && checked1) {
      setStep(2);
    } else if (step === 2 && checked2) {
      onConfirm();
      handleClose();
    }
  };

  // Determine if we have multiple conflicts
  const hasTimeConflict = conflicts.some(c => c.type === 'time');
  const hasTechnicianConflict = conflicts.some(c => c.type === 'technician');
  const conflictCount = conflicts.length;

  // Get conflict details
  const timeConflict = conflicts.find(c => c.type === 'time');
  const technicianConflict = conflicts.find(c => c.type === 'technician');

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#fff3e0' }}>
        <WarningAmberIcon color="warning" />
        <Typography variant="h6">
          {step === 1 ? `Warning: ${conflictCount} Preference Mismatch${conflictCount > 1 ? 'es' : ''} (Step 1/2)` : 'Final Confirmation (Step 2/2)'}
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {hasTimeConflict && (
              <Chip 
                icon={<AccessTimeIcon />} 
                label="Time Mismatch" 
                color="warning" 
                size="small" 
              />
            )}
            {hasTechnicianConflict && (
              <Chip 
                icon={<PersonIcon />} 
                label="Technician Mismatch" 
                color="error" 
                size="small" 
              />
            )}
          </Box>
        </Alert>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Appointment:
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {appointmentInfo}
          </Typography>
        </Box>

        {/* Technician Conflict */}
        {hasTechnicianConflict && technicianConflict && (
          <>
            <Box sx={{ mb: 2, p: 2, bgcolor: '#ffebee', borderRadius: 1, border: '2px solid #f44336' }}>
              <Typography variant="subtitle2" color="error.dark" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon fontSize="small" />
                Customer's Preferred Technician:
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {technicianConflict.preferred}
              </Typography>
            </Box>

            <Box sx={{ mb: 2, p: 2, bgcolor: '#fff3e0', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="warning.dark">
                You are assigning to:
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {technicianConflict.actual}
              </Typography>
            </Box>
          </>
        )}

        {/* Time Conflict */}
        {hasTimeConflict && timeConflict && (
          <>
            <Box sx={{ mb: 2, p: 2, bgcolor: '#e3f2fd', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccessTimeIcon fontSize="small" />
                Customer's Preferred Time:
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {timeConflict.preferred || preferredTimeInfo}
              </Typography>
            </Box>

            <Box sx={{ mb: 2, p: 2, bgcolor: '#fff3e0', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="warning.dark">
                You are scheduling for:
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {timeConflict.actual || newTimeInfo}
              </Typography>
            </Box>
          </>
        )}

        {step === 1 ? (
          <FormControlLabel
            control={
              <Checkbox
                checked={checked1}
                onChange={(e) => setChecked1(e.target.checked)}
                color="warning"
              />
            }
            label={
              <Typography variant="body2">
                I understand that this does not match the customer's preference
                {conflictCount > 1 ? 's' : ''}
              </Typography>
            }
          />
        ) : (
          <FormControlLabel
            control={
              <Checkbox
                checked={checked2}
                onChange={(e) => setChecked2(e.target.checked)}
                color="error"
              />
            }
            label={
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                I confirm that I want to proceed anyway
              </Typography>
            }
          />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleNext}
          variant="contained"
          color={step === 1 ? 'warning' : 'error'}
          disabled={step === 1 ? !checked1 : !checked2}
        >
          {step === 1 ? 'Continue to Step 2' : 'Confirm Assignment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PreferredTimeConfirmDialog;
